import React, { memo, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { map } from 'lodash/fp'
import Dropdown from '../../components/Dropdown'
import useThunkDispatch from '../../common/useThunkDispatch'
import { currentUserSelector } from '../../auth/authSelectors'
import { query } from '../../common/api'
import { MANAGER_AVAILABLE_ACCOUNTS_QUERY, USER_REASSIGN_ACCOUNT_MUTATION } from '../managerQueries'
import { IAccount } from '../../types/user'
import { requestAccountBranches, requestManagerBranches } from '../managerActions'
import { requestCurrentUser, setToken } from '../../auth/authActions'
import { STORAGE_USER_TOKEN_KEY } from '../../types/user'

interface IPickerOption {
  label: string
  value: number
}

interface IOwnProps {
  className?: string
}

const ManagerAccountsPicker: React.FC<IOwnProps> = ({ className }) => {
  const dispatch = useThunkDispatch()
  const currentUser = useSelector(currentUserSelector)
  const [accounts, setAccounts] = React.useState<IAccount[]>([])

  const value = useMemo(() => {
    const current = accounts.find((a) => a.id === currentUser.account?.id)
    return current ? { value: current.id, label: current.name } : undefined
  }, [accounts, currentUser])

  const handleAccountSelect = useCallback(
    async (option: IPickerOption) => {
      const accountId = option.value

      const resp = await query(USER_REASSIGN_ACCOUNT_MUTATION, {
        id: currentUser.id,
        accountId,
      })

      const token: string | undefined = resp?.userReassignAccount?.token
      if (token) {
        localStorage.setItem(STORAGE_USER_TOKEN_KEY, token)
        dispatch(setToken(token))
      }

      await dispatch(requestCurrentUser())
      dispatch(requestManagerBranches())
      dispatch(requestAccountBranches())
    },
    [accounts, currentUser],
  )

  useEffect(() => {
    (async () => {
      const resp = await query(MANAGER_AVAILABLE_ACCOUNTS_QUERY)
      const list: IAccount[] = resp?.manager?.availableAccounts || []
      setAccounts(list)
    })()
  }, [])

  if (!accounts.length) return null

  const accountOptions = map(
    (a: IAccount): IPickerOption => ({ value: a.id, label: a.name }),
    accounts,
  )

  return (
    <div className={className}>
      <Dropdown
        onChange={handleAccountSelect}
        className="-top"
        value={value}
        searcheable={false}
        itemToString={(value) => (value ? value.label : 'Pick one...')}
        options={accountOptions}
        width="auto"
      />
    </div>
  )
}

export default memo(ManagerAccountsPicker)
